import {
  apiGetRoomInfo,
  apiGetMemberList,
  apiUpdateAnchorPlayTimeStatus,
  apiGetSDKToken,
  apiGetMinSDKToken,
  apiUpdateRoomInfo,
  apiUpdateRoomInfoByAdmin,
  apikikUser,
  apiSetManager,
  apiRoomOut
} from '@/api/room'
import type {
  roomInfoInterface,
  memberListInterface,
  chatListInterface
} from '~/types/apiType/roomType'
import type { userInfoInterface } from '@/types/apiType/userType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
import type { socketRoomInfoInterface } from '@/types/viewsType/roomType'
import { ROOM_TYPE } from '@/utils/constant'
import { apiRelationAdd } from '~/api/user'

const TIMEOUT_TIME = 14000 // 常量 定时器倒计时

export const useRoomStore = defineStore({
  id: 'room',
  state: () => ({
    roomType: ROOM_TYPE.VIDEO_LIVE as ROOM_TYPE, // 房间类型 5:音频直播房，9：视频直播房，10：视频通话房
    roomId: 0 as number, // 房间ID
    roomUid: 0 as number, // 房间用户UID
    onlineNum: 0, // 在线人数
    roomInfo: {} as roomInfoInterface, // 房间信息
    anchorInfo: {} as userInfoInterface, // 主播个人信息，通过接口获取
    getMemberListTime: 0 as number, // 上一次获取成员列表的时间
    memberList: [] as memberListInterface[], // 房间成员列表
    chatList: [] as chatListInterface[], // 会话列表
    chatGiftList: [] as chatListInterface[], // 会话礼物列表
    chatTextList: [] as chatListInterface[], // 会话文本列表
    isShowMask: true as boolean, // 是否显示遮罩层，用作视频还没加载时显示
    // 超时功能，发布超时,监听10s后是否发布成功定时器,rtc情况下才去监听
    timeOut: {
      isTimeout: 0 as number, // 超时定时器Id
      isPublishTimeout: false as boolean, // 监听发布是否成功
      isPirstFrameTimeout: false as boolean // 监听远程用户首帧是否成功
    },
    isShowLiveRouteLeavDialog: true as boolean, // 是否显示直播页面离开页面确认弹窗
    isHostLive: false as boolean, // 房主是否在直播
    roomPasswordDialogConfig: {
      //房间密码入口对话框
      isShow: false as boolean,
      password: '' // 密码
    },
    quitRoomType: 0 as 0 | 1 | 2 | 3, // 退出房间类型, 0:显示RoomSheet，1：直接退出，2：最小化退出，3：通话房间退出
    isShowRoomSheet: false, // 是否显示房间Sheet
    isNewEnterRoom: true, // 是否是新进入的房间，不是就是最小化进房
    socketRoomInfo: {} as socketRoomInfoInterface, // socket房间信息
    dayGold: 0, //房间内送礼金额
    dayDiamond: 0 // 视频房内钻石收入
  }),
  getters: {
    // 主播信息，优先从公共缓存数据从获取
    anchorInfoStorage: (state): userInfoStorageMapInterface => {
      const userStore = useUserStore()
      return userStore.userInfoStorageMap[state.roomUid] || state.anchorInfo
    }
  },
  actions: {
    /**
     * 设置socket房间信息
     */
    setSocketRoomInfo(value) {
      // FIXME: 视频房同步客户端的，视频房只用声网SDK
      if (this.roomType === constant.ROOM_TYPE.VIDEO_LIVE) {
        value.audioChannel = 1
      }
      this.socketRoomInfo = value
      const publicStore = usePublicStore()
      // 房间信息更新时，更新roomInfo
      this.roomInfo = { ...this.roomInfo, ...value }
      // 房间信息更新后通知客户端
      publicStore.isIOSWebView && window['appFun']('getRoomInfo', this.roomInfo)
      // 关闭魅力值显示开关时，清空魅力值
      if (!this.socketRoomInfo.charmSwitch) {
        const audioStore = useAudioStore()
        audioStore.onRoomMicCharmPush({})
      }
    },
    /**
     * 语音房初始化设置roomId,roomUid,临时使用
     */
    setRoomAppId(roomId: number, roomUid: number) {
      this.roomId = roomId
      this.roomUid = roomUid
    },
    //#region 获取腾讯签名参数相关
    // 获取腾讯签名参数
    async getSDKToken() {
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      const roomStore = useRoomStore()
      const params = { roomId: roomStore.roomId }
      const res = await apiGetSDKToken(
        // FIXME: 视频房同步客户端的，视频房只用声网SDK
        this.roomType === ROOM_TYPE.VIDEO_LIVE ? { ...params, type: 1 } : params
      )
      const { data, code } = res
      if (code === 200) {
        if (data.rtcChannel === 'AGORA') {
          agoraStore.setAgoraSDKParams(data.appId, data.token)
        } else {
          trtcStore.setTrtcSDKParams(
            Number(data.appId),
            data.token,
            data.privateMapKey
          )
        }
      }
    },
    // 获取腾讯上麦签名参数
    async getSDKMinToken() {
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      const roomStore = useRoomStore()
      const res = await apiGetMinSDKToken({ roomId: roomStore.roomId })
      const { data, code } = res
      if (code === 200) {
        if (data.rtcChannel === 'AGORA') {
          agoraStore.setAgoraSDKMinParams(data.token)
        } else {
          trtcStore.setTrtcSDKMinParams(data.privateMapKey)
        }
      }
    },
    //#endregion
    /**
     * 设置显示房间显示房间Sheet
     * @param {0 | 1 | 2 | 3} value 0:显示RoomSheet，1：直接退出，2：最小化退出，3：通话房间退出
     **/
    setQuitRoomType(value: 0 | 1 | 2 | 3) {
      this.quitRoomType = value
    },
    /**
     * 设置显示房间显示房间Sheet
     * @param {boolean} value 是否显示
     **/
    setIsShowRoomSheet(value: boolean) {
      this.isShowRoomSheet = value
    },
    /**
     * 更新用户在房计时在线时长
     * @param {1|2} type 主播直播时长统计状态：1-暂停；2-恢复
     * */
    async updateUserTimeStatus(type: 1 | 2) {
      const socketStore = useSocketStore()
      const publicStore = usePublicStore()
      if (this.roomId && socketStore.isEnterRoom) {
        await apiUpdateAnchorPlayTimeStatus(
          {
            roomId: this.roomId,
            playTimeStatus: type
          },
          publicStore.isIOSWebView && type === 1
        )
      }
    },
    /**
     * 设置显示房间密码入口对话框
     */
    setRoomPasswordDialogConfig(config: {
      isShow: boolean
      password?: string
    }) {
      console.log(config.isShow)
      this.roomPasswordDialogConfig.isShow = config.isShow
      if (config.password || config.password === '') {
        this.roomPasswordDialogConfig.password = config.password
      }
    },
    /**
     * 判断主播是否在直播,用于直播SDK判断
     * @param {Array<number | string>} ids 远程用户
     */
    whetherHostIsLive(ids: Array<number | string>) {
      const roomStore = useRoomStore()
      const userStore = useUserStore()
      // 用临时数据保存是为了区分开播，还是由未开播变成开始直播
      const temporaryIsLive =
        ids.some((id) => {
          return id == roomStore.roomInfo.uid
        }) ||
        roomStore.memberList.some((item) => item.uid == roomStore.roomInfo.uid)

      // 房主由未开播变成开始直播
      if (!this.isHostLive && temporaryIsLive) {
        roomStore.onStartLive()
      }
      // 如果是房主自己打开房间直接设置在线
      this.isHostLive =
        roomStore.roomInfo.uid === userStore.userInfo.uid
          ? true
          : temporaryIsLive
    },
    /**
     * 设置主播是否在直播
     * @param {boolean} value 房间ID
     */
    setIsHostLive(value: boolean) {
      this.isHostLive = value
      // 如果房主开始直播或复播
      if (this.isHostLive) {
        this.onStartLive()
      }
    },
    updateDayGold(value: number) {
      this.dayGold = value
    },
    updateDayDiamond(value: number) {
      this.dayDiamond = value
    },
    /**
     * 去直播间，因需要检测是否支持SDK，故去直播间一定要调此API
     * @param {string | number} roomId 房间ID
     * @param {string | number} roomUid 房主UID
     * @param {5 |9} type 房间类型
     */
    async toLiveRoom(
      roomId: string | number,
      roomUid: string | number,
      type: 5 | 9
    ) {
      const router = useRouter()
      const audioStore = useAudioStore()
      const scoketStore = useSocketStore()

      // 如果进房时 this.roomId 有值就是最小化了，不同 roomId 就是新入房
      this.isNewEnterRoom = !this.roomId ? true : !(this.roomId === roomId)
      if (this.isNewEnterRoom) {
        // 从点击动作进去房间就打开扬声器，因浏览器自动播放策略
        audioStore.resetData()
        audioStore.setIsOpenSound(true)
        this.roomId && scoketStore.sendExitChatRoom()
      }

      setTimeout(() => {
        if (type === constant.ROOM_TYPE.AUDIO_LIVE) {
          router.push({
            path: '/room/audio',
            query: {
              roomId,
              roomUid: common.encryptParam(String(roomUid))
            }
          })
        } else {
          // 视频房
          router.push({
            path: '/room/live',
            query: {
              roomId,
              roomUid: common.encryptParam(String(roomUid))
            }
          })
        }
      }, 200)
    },
    /**
     * 主播开始直播监听 开播和复播
     * */
    onStartLive() {
      const i18n = useNuxtApp().$i18n
      if (this.roomType === constant.ROOM_TYPE.VIDEO_LIVE) {
        const roomStore = useRoomStore()
        // 如果是视频通话 监听到远程用户就向公屏发送 等待消息
        roomStore.updateChatList('put', {
          content: i18n.t('room.joinWaitingChatTips'),
          type: constant.ROOM_CHAT_TYPE.PROMPT_TEXT
        })
      }
    },
    /**
     * 设置是否显示直播页面离开页面确认弹窗
     * @param {boolean} value 开关
     */
    setIsShowLiveRouteLeavDialog(value: boolean) {
      this.isShowLiveRouteLeavDialog = value
    },
    // 设置超时定时器
    setIsTimeout() {
      const i18n = useNuxtApp().$i18n
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
        !this.timeOut.isTimeout
      ) {
        this.timeOut.isTimeout = window.setTimeout(() => {
          showToast(i18n.t('call.connectionFailed'))
          console.log(
            'AgoraSDK => 超时退出',
            `发布：${this.timeOut.isPublishTimeout}，首帧：${this.timeOut.isPirstFrameTimeout}`
          )
          const callStore = useCallStore()
          callStore.onCallClose('')
        }, TIMEOUT_TIME)
      }
    },
    // 关闭超时定时器
    closeIsTimeout(config: {
      isPublishTimeout?: boolean
      isPirstFrameTimeout?: boolean
    }) {
      if (config.isPublishTimeout) {
        this.timeOut.isPublishTimeout = true
      }
      if (config.isPirstFrameTimeout) {
        this.timeOut.isPirstFrameTimeout = true
      }
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
        this.timeOut.isTimeout &&
        this.timeOut.isPublishTimeout &&
        this.timeOut.isPirstFrameTimeout
      ) {
        console.log(
          'AgoraSDK => 关闭超时退出',
          `发布：${this.timeOut.isPublishTimeout}，首帧：${this.timeOut.isPirstFrameTimeout}`
        )
        this.timeOut.isTimeout && window.clearTimeout(this.timeOut.isTimeout)
      }
    },
    // 设置遮罩层
    setIsShowMask(show: boolean) {
      this.isShowMask = show
    },

    /**
     * 更新会话列表
     * @param {string} type init:初始化,put
     * */
    updateChatList(type: string, obj?: chatListInterface) {
      switch (type) {
        case 'init':
          this.chatList = []
          this.chatTextList = []
          this.chatGiftList = []
          break
        case 'put':
          this.chatList.push(obj as chatListInterface)
          if (obj?.type === constant.ROOM_CHAT_TYPE.CHAT_TEXT) {
            // 会话文本
            this.chatTextList.push(obj as chatListInterface)
          } else if (obj?.type === constant.ROOM_CHAT_TYPE.CHAT_GIFT) {
            // 会话礼物
            this.chatGiftList.push(obj as chatListInterface)
          }
          break
        default:
          break
      }
    },
    /**
     * 踢出房间
     */
    kickOutRoom(member: memberListInterface | userInfoStorageMapInterface) {
      const i18n = useNuxtApp().$i18n
      const publicStore = usePublicStore()
      /**
       * 封禁
       * @param timeUnit 封禁单位,1-天，2-小时
       * @param kickOutDay 对应时长
       * @param message 提示内容
       */
      const callback = (data: {
        timeUnit: number
        kickOutDay: number
        message: string
      }) => {
        const { timeUnit, kickOutDay, message } = data
        const roomId = this.roomId
        const targetUid = member.uid
        showConfirmDialog({
          message: message,
          confirmButtonText: i18n.t('public.Yes'),
          cancelButtonText: i18n.t('public.No'),
          className: 'private-dialog-customize private-dialog-customize-IOS'
        })
          .then(() => {
            apikikUser({ roomId, targetUid, timeUnit, kickOutDay }).then(
              (res) => {
                if (res.code !== 200) {
                  showToast(res.message)
                }
              }
            )
          })
          .catch(() => {})
      }
      publicStore.setActionSheetConfig({
        isShow: true,
        options: [
          {
            name: i18n.t('room.KickOut1Hour'),
            callback: () => {
              callback({
                kickOutDay: 1,
                timeUnit: 2,
                message: i18n.t('room.KickOut1HourTips', [member.erbanNo])
              })
            }
          },
          {
            name: i18n.t('room.KickOut1Day'),
            callback: () => {
              callback({
                kickOutDay: 1,
                timeUnit: 1,
                message: i18n.t('room.KickOut1DayTips', [member.erbanNo])
              })
            }
          },
          {
            name: i18n.t('room.KickOut3Day'),
            callback: () => {
              callback({
                kickOutDay: 3,
                timeUnit: 1,
                message: i18n.t('room.KickOut3DayTips', [member.erbanNo])
              })
            }
          },
          {
            name: i18n.t('room.KickOut7Day'),
            callback: () => {
              callback({
                kickOutDay: 7,
                timeUnit: 1,
                message: i18n.t('room.KickOut7DayTips', [member.erbanNo])
              })
            }
          }
        ]
      })
    },
    /**
     * 资料卡片打开更多操作(房主/管理员)
     * @param uid 目标用户ID
     */
    handleOpenMoreAction(uid: number) {
      const i18n = useNuxtApp().$i18n
      const publicStore = usePublicStore()
      const audioStore = useAudioStore()
      const userStore = useUserStore()

      const item = audioStore.queueList.find(
        (item) => item.value.member?.uid === uid
      )
      const member = item?.value.member || userStore.userInfoStorageMap[uid]
      // 用户麦位位置
      const micPos =
        item?.key ??
        (audioStore.hostMinInfo.value.member?.uid === uid ? -1 : undefined)
      // 是否在麦
      const isInMic = micPos !== undefined

      // 打开操作选项
      const options = [
        // 下麦 / 强制下麦
        {
          name: i18n.t('room.DownMic1'),
          callback: () => {
            audioStore.pollQueue(micPos)
          },
          verify: () => isInMic
        },
        // 邀请
        {
          name: i18n.t('room.Invite'),
          callback: () => {
            const nullMicPos = audioStore.queueList.findIndex(
              (item) => !item.value.member
            )
            if (nullMicPos >= 0) {
              audioStore.setInviteMicPosition(nullMicPos)
              audioStore.sendInviteUserUpMic(uid)
            }
          },
          verify: () => !isInMic
        },
        // 踢出房间
        {
          name: i18n.t('room.KickOut'),
          callback: () => {
            setTimeout(() => {
              this.kickOutRoom(member)
            }, 300)
          },
          verify: () => true
        },
        // 设置管理员/取消
        {
          name: (member as any).is_manager
            ? i18n.t('room.CancelAdmin')
            : i18n.t('room.SetManager'),
          callback: () => {
            apiSetManager({
              room_id: this.roomId,
              is_add: !(member as any).is_manager ? 1 : 0,
              account: uid
            }).then((res) => {
              showToast(res.message)
            })
          },
          verify: () => member && audioStore.isCreator
        },
        // 加入黑名单
        {
          name: i18n.t('room.AddBlacklist'),
          callback: () => {
            showConfirmDialog({
              message: i18n.t('room.ConfirmBlackListTips', [member.nick]),
              confirmButtonText: i18n.t('public.Confirm'),
              cancelButtonText: i18n.t('editProfile.Cancle'),
              className:
                'private-dialog-customize private-dialog-customize-IOS add-black-list'
            }).then(() => {
              apiRelationAdd({
                roomId: this.roomId,
                isAdd: 1,
                targetUid: member.uid
              }).then((res) => {
                showToast(res.message)
              })
            })
          },
          verify: () =>
            member &&
            (audioStore.isCreator ||
              (audioStore.isManager && !(member as any)?.is_manager)) &&
            member.uid !== userStore.userInfo.uid
        }
      ]
      publicStore.setActionSheetConfig({
        isShow: true,
        options: options.filter((item) => item.verify())
      })
    },
    // 初始化房间
    async initRoom(info: {
      roomType: ROOM_TYPE
      roomId?: number
      roomUid: number
    }) {
      this.setQuitRoomType(0)
      // // 如果是新进房，初始化数据
      // if (this.isNewEnterRoom) {
      //   this.resetData()
      // }
      const publicStore = usePublicStore()
      this.roomType = info.roomType
      this.roomId = info.roomId || 0
      this.roomUid = info.roomUid
      this.getMemberListTime = 0
      this.onlineNum = 0
      console.log(
        '房间初始化 => 初始化',
        `roomType:${this.roomType},roomId:${this.roomId},roomUid:${this.roomUid}`
      )
      // 进入直播页面同时重置 isShowLiveRouteLeavDialog
      this.setIsShowLiveRouteLeavDialog(true)

      // 视频直播房 | 语音房
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        this.roomType === constant.ROOM_TYPE.AUDIO_LIVE
      ) {
        await this.getRoomInfo()
        this.getAnchorInfo()
        if (this.isNewEnterRoom) {
          // iOS语音房在入口文件调socket进房
          if (!publicStore.isIOSWebView) {
            this.socketEnterRoom()
          }
        } else {
          // 如果是最小化进来就恢复计时
          this.updateUserTimeStatus(2)
        }

        // 调接口SDK token
        this.getSDKToken()
      }
      // 视频通话房
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
        this.socketEnterRoom()
      }
    },
    // socket进房成功回调
    onEnterRoomSuccess(data) {
      console.log('--------------onEnterRoomSuccess', new Date().toISOString())
      const callStore = useCallStore()
      const publicStore = usePublicStore()
      const detonatingStore = useDetonatingStore()
      const roomPkStore = useRoomPkStore()
      const audioStore = useAudioStore()
      const userStore = useUserStore()
      userStore.getUserBannedStatus()

      this.setSocketRoomInfo(data.room_info)
      // 通知iOS更新roomId
      if (publicStore.isIOSWebView) {
        window['appFun']('updateRoomId', { roomId: data.room_info.roomId })
      }
      emitter.emit('enterRoomSuccess')

      //设置房间内送礼金额
      this.dayGold = data.additional.dayGold
      this.dayDiamond = data.additional.dayDiamond
      detonatingStore.setDetonatingInfo(data.additional)
      // 获取房间游戏配置、爆弹各等级达标总值
      publicStore.getClientConfigure()

      // 判断房主是否在直播
      const isLive = data.queue_list.some((v) => {
        return v.value.member && v.value.member.uid === this.roomUid
      })
      this.setIsHostLive(isLive)

      // 埋点，判断是否是通话房
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
        // 如果是视频通话
        // if (callStore.callType === 1) {
        //   eventTracking.eventLog(eventTracking.eventId.enter_private_room) // 进入1v1视频通话房间成功
        // } else {
        //   // 如果是匹配通话
        //   eventTracking.eventLog(eventTracking.eventId.enter_match_room) // 进入匹配视频通话房间成功
        // }
        // 调rtc进房监听
        this.onRtcGetRoomInfo(data.room_info)
      } else {
        // 是直播间
        eventTracking.eventLog(eventTracking.eventId.enter_room) // 进入直播房间成功
      }

      // 如果房间已经开始PK中，初始化PK信息
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
        Object.keys(data.roomInfoExtend.pkInfo).length > 0
      ) {
        const member =
          audioStore.queueList[constant.ROOM_PK_MIC_INDEX]?.value?.member ||
          null
        roomPkStore.initRoomPkInfo(data.roomInfoExtend.pkInfo, {
          agoraKey: '',
          runningRecordId: 0,
          timeStamp: 0,
          avatar: member?.avatar || '',
          erbanNo: member?.erbanNo?.toString() || '',
          nick: member?.nick || '',
          otherAgoraKey: '',
          pkRoomId: data.roomInfoExtend.pkInfo.otherRoomId,
          pkRoomUid: member?.uid || 0
        })
      }

      // 成功隔壁输入密码弹窗
      this.setRoomPasswordDialogConfig({ isShow: false })

      // 开始计算用户在房计时在线时长
      this.updateUserTimeStatus(2)
    },
    // 通话房，获取房间信息
    async onRtcGetRoomInfo(info) {
      this.roomId = info.roomId
      await this.getRoomInfo()
      this.getMemberList()
    },
    // live获取房间信息
    async getRoomInfo() {
      const res = await apiGetRoomInfo({ roomId: this.roomId })
      const { code, data } = res
      if (code === 200) {
        // FIXME: 视频房同步客户端的，视频房只用声网SDK
        if (this.roomType === constant.ROOM_TYPE.VIDEO_LIVE) {
          data.audioChannel = 1
        }
        this.roomInfo = data
      }
    },
    /**
     * socket进入直播状态的房间
     */
    socketEnterRoom() {
      const socketStore = useSocketStore()
      if (
        (this.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
          this.roomType === constant.ROOM_TYPE.AUDIO_LIVE) &&
        // this.roomId &&
        this.roomUid
      ) {
        // live房
        console.log('live进入房间')
        console.log(
          '-------------- send socketEnterRoom',
          new Date().toISOString()
        )
        // this.roomInfo.roomPwd
        socketStore.snedEnterWithOpenChatRoom({
          uid: this.roomUid,
          type: this.roomType,
          pwd: this.roomPasswordDialogConfig.password,
          avatar: this.anchorInfoStorage.avatar,
          title: this.roomInfo.title
        })
      }
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL && this.roomUid) {
        // 通话房
        console.log('rtc进入房间')
        socketStore.snedEnterWithOpenChatRoom({
          uid: this.roomUid,
          type: 10
        })
      }
    },
    /**
     * 设置房间配置开关
     * @param isOpen 是否开启
     * @param type chatZone.聊天公屏 rideEffect.座驾进房特效 lowValueGift.低价值礼物特效
     */
    async setRoomConfigSwitch(
      isOpen: boolean,
      type: 'chatZone' | 'rideEffect' | 'lowValueGift'
    ) {
      const map: Record<
        typeof type,
        { key: string; openCode: number; closeCode: number }
      > = {
        chatZone: { key: 'publicChatSwitch', openCode: 153, closeCode: 154 },
        rideEffect: {
          key: 'showOtherEnterAnimSwitch',
          openCode: 157,
          closeCode: 158
        },
        lowValueGift: { key: 'giftEffectSwitch', openCode: 155, closeCode: 156 }
      }
      const audioStore = useAudioStore()
      if (audioStore.isCreator) {
        const { code, message } = await apiUpdateRoomInfo({
          roomType: this.roomType,
          roomUid: this.roomUid,
          [map[type].key]: isOpen ? 0 : 1
        })
        if (code !== 200) {
          showToast(message)
          return
        }
      } else if (audioStore.isManager) {
        const { code, message } = await apiUpdateRoomInfoByAdmin({
          roomType: this.roomType,
          roomUid: this.roomUid,
          [map[type].key]: isOpen ? 0 : 1
        })
        if (code !== 200) {
          showToast(message)
          return
        }
      } else {
        return
      }
      // 设置房间信息成功、推送im
      const socketStore = useSocketStore()
      const userStore = useUserStore()
      socketStore.privateSend(
        {
          route: socketRequestRoute.sendMessage,
          req_data: {
            custom: JSON.stringify({
              data: {
                handleIsInvisible: userStore.userInfo.isInvisible,
                handleVipDate: userStore.userInfo.vipDate,
                handleVipId: userStore.userInfo.vipId,
                isAuctionList: userStore.userInfo,
                micPosition: 0,
                targetIsInvisiable: this.anchorInfo.isInvisible,
                targetVipDate: this.anchorInfo.vipDate,
                targetVipId: this.anchorInfo.vipId,
                uid: userStore.userInfo.uid
              },
              first: 8,
              second: isOpen ? map[type].openCode : map[type].closeCode
            }),
            member: JSON.stringify(userStore.userInfo),
            room_id: this.roomId
          }
        },
        true
      )
    },
    // 获取主播信息
    // 如果有uid过来的，就优先拿uid,目前有uid过来的是拨打或匹配
    async getAnchorInfo(uid?: number) {
      const userStore = useUserStore()
      if (!(uid || this.roomUid)) return
      userStore.publicGetUserInfo(uid || this.roomUid).then((res) => {
        this.anchorInfo = res
        if (uid) {
          console.log('开始预加载头像')
          // 如果需要头像预加载，为封面做准备
          new Image().src = this.anchorInfo.avatar as string
        }
      })
    },
    // 获取右上角房间成员
    async getMemberList(force?: boolean) {
      if (!this.roomId) {
        return
      }
      // 直播房上次和下次获取时间相差10秒才获取，RTC模式直接刷新
      const nowTime = new Date().getTime()
      if (
        nowTime - this.getMemberListTime >= 10000 ||
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
        force
      ) {
        this.getMemberListTime = nowTime
        const res = await apiGetMemberList({
          room_id: this.roomId,
          start: 1,
          limit: 20
        })
        const { code, data } = res
        if (code === 200) {
          this.memberList = [...data.vip_member_list, ...data.member_list]
          const userStore = useUserStore()
          userStore.updateUserInfoStorageMap({
            type: 'put',
            list: [...this.memberList]
          })
          this.onlineNum = this.memberList.length
        }
      }
    },
    /**
     * socket更新右上角房间成员
     * @param {1|2} type 1: 添加,2：移除
     * @param {number} uid 房间成员Uid
     * @param {memberListInterface} memberUser 房间成员用户信息
     */
    updateMemberList(
      type: 1 | 2,
      uid: number,
      memberUser?: memberListInterface
    ) {
      const index = this.memberList.findIndex((v) => v.uid == uid)
      if (type === 1 && index === -1 && memberUser) {
        // 添加，是vip往首位添加
        if (memberUser.vipId) {
          this.memberList.unshift(memberUser)
        } else {
          this.memberList.push(memberUser)
        }
      } else if (type === 2) {
        // 移除
        this.memberList.splice(index, 1)
      }
    },
    /**
     * 被踢出房间
     */
    async kickedOutRoom() {
      // 把确认退出确认框先关闭
      const publicStore = usePublicStore()
      const callStore = useCallStore()
      callStore.setIsShowCallRouteLeavDialog(false)
      this.setIsShowLiveRouteLeavDialog(false)
      // 被踢出房间,重置到首页
      if (publicStore.isIOSWebView) {
        setTimeout(async () => {
          await this.quit()
          window['appFun']('closeRoom')
        }, 2000)
      } else {
        const router = useRouter()
        router.replace('/')
      }
    },

    // 退出
    async quit() {
      if (this.roomId && this.roomUid) {
        await this.updateUserTimeStatus(1)
        const giftStore = useGiftStore()
        giftStore.resetRoomData()
        if (
          this.quitRoomType === 0 ||
          this.quitRoomType === 1 ||
          this.quitRoomType === 3
        ) {
          const socketStore = useSocketStore()
          socketStore.sendExitChatRoom()
          // 记录退出房间
          await apiRoomOut({
            roomUid: this.roomId,
            roomType: this.roomType
          })
        }
        this.resetData()
      }
    },
    /**
     * 重置数据
     * @param {boolean} isForce 是否强制重置roomId
     * */
    resetData() {
      // 强制退出才清除
      if (
        this.quitRoomType === 0 ||
        this.quitRoomType === 1 ||
        this.quitRoomType === 3
      ) {
        this.roomId = 0
        this.roomUid = 0
        this.roomInfo = common.defaultRoomInfo
        this.socketRoomInfo = common.defaultSocketRoomInfo
        this.memberList = []
        this.onlineNum = 0
        this.anchorInfo = common.defaultInfo
        this.updateChatList('init')
        this.isShowMask = true
      }

      // 清除定时器
      this.timeOut.isPublishTimeout = false
      this.timeOut.isPirstFrameTimeout = false
      this.timeOut.isTimeout && window.clearTimeout(this.timeOut.isTimeout)
      this.timeOut.isTimeout = 0

      this.roomPasswordDialogConfig.password = ''
      this.isShowRoomSheet = false
    }
  },
  persist: {
    key: 'roomStore',
    paths: ['roomInfo', 'anchorInfo']
  }
})
